import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useMode } from '../contexts/ModeContext';
import ModeToggle from '../components/ModeToggle';
import './SettingsPage.css';

function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const { isAdvancedMode } = useMode();
  const [themePreference, setThemePreference] = useState('auto');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.title = 'PEC - Configuración';
    // Load preferences from localStorage
    const savedThemePref = localStorage.getItem('themePreference') || 'auto';
    setThemePreference(savedThemePref);
  }, []);

  const handleThemeChange = (theme) => {
    setThemePreference(theme);
    localStorage.setItem('themePreference', theme);
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Configuración</h1>
            <p>Personaliza tu experiencia y administra preferencias del sistema</p>
          </div>
          <div className="header-center">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21L16.514 16.506M19 10.5A8.5 8.5 0 1110.5 2a8.5 8.5 0 018.5 8.5z" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="search"
                  placeholder="Buscar configuraciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>
          <div className="header-right">
            <button className="create-action-button">
              💾 EXPORTAR CONFIG
            </button>
            <button 
              onClick={toggleTheme}
              className="theme-toggle" 
              title={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-container">
          <div className="settings-section">
            <div className="section-header">
              <h2>🎨 Apariencia y Tema</h2>
              <p className="section-description">
                Personaliza la interfaz visual de la aplicación según tus preferencias.
              </p>
            </div>
            
            <div className="setting-options">
              <label className="setting-option">
                <input
                  type="radio"
                  name="theme"
                  value="light"
                  checked={themePreference === 'light'}
                  onChange={(e) => handleThemeChange(e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">☀️</div>
                  <div className="option-info">
                    <div className="option-title">Tema Claro</div>
                    <div className="option-description">
                      Interfaz limpia y brillante perfecta para uso diurno.
                    </div>
                  </div>
                </div>
              </label>

              <label className="setting-option">
                <input
                  type="radio"
                  name="theme"
                  value="dark"
                  checked={themePreference === 'dark'}
                  onChange={(e) => handleThemeChange(e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">🌙</div>
                  <div className="option-info">
                    <div className="option-title">Tema Oscuro</div>
                    <div className="option-description">
                      Suave para los ojos en ambientes con poca luz.
                    </div>
                  </div>
                </div>
              </label>

              <label className="setting-option">
                <input
                  type="radio"
                  name="theme"
                  value="auto"
                  checked={themePreference === 'auto'}
                  onChange={(e) => handleThemeChange(e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">🔄</div>
                  <div className="option-info">
                    <div className="option-title">Automático (Sistema)</div>
                    <div className="option-description">
                      Se adapta automáticamente a la configuración de tu sistema.
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <h2>⚙️ Application Mode</h2>
              <p className="section-description">
                Choose between Simple personal finance mode or Advanced multi-entity platform.
              </p>
            </div>
            
            <div className="mode-toggle-section">
              <ModeToggle />
              <div className="mode-description-text">
                <p>
                  <strong>{isAdvancedMode ? 'Advanced Mode' : 'Simple Mode'}:</strong> {' '}
                  {isAdvancedMode 
                    ? 'Multi-entity platform with consolidated financial management across multiple businesses, investments, and personal finances.'
                    : 'Personal finance mode focused on individual income, expenses, and basic financial tracking.'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <h2>✍️ Editor de Texto</h2>
              <p className="section-description">
                Configuración y características del editor de notas integrado.
              </p>
            </div>
            <div className="about-content">
              <div className="feature-highlight">
                <div className="feature-icon">📝</div>
                <div className="feature-info">
                  <h4>ReactQuill Rich Text Editor</h4>
                  <p>Tu aplicación de notas utiliza ReactQuill, un potente editor de texto enriquecido con capacidades avanzadas de formato y rendimiento optimizado.</p>
                </div>
              </div>
              
              <div className="editor-features">
                <h4>🚀 Características Principales:</h4>
                <div className="features-grid">
                  <div className="feature-item">
                    <span className="feature-bullet">🔤</span>
                    <span>Formato de texto enriquecido (negrita, cursiva, subrayado)</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-bullet">📋</span>
                    <span>Encabezados y estilos de texto</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-bullet">📝</span>
                    <span>Listas con viñetas y numeradas</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-bullet">🔗</span>
                    <span>Enlaces y embedding de medios</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-bullet">⚡</span>
                    <span>Desplazamiento suave y soporte para rueda del mouse</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-bullet">📋</span>
                    <span>Copiar/pegar desde otras aplicaciones</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <h2>📊 Sistema y Rendimiento</h2>
              <p className="section-description">
                Configuraciones avanzadas del sistema y optimización.
              </p>
            </div>
            <div className="system-stats">
              <div className="stat-item">
                <span className="stat-label">💾 Almacenamiento Local:</span>
                <span className="stat-value">2.4 MB utilizados</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">📝 Total de Notas:</span>
                <span className="stat-value">23 documentos</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">🔧 Versión del Editor:</span>
                <span className="stat-value">ReactQuill 1.3.5</span>
              </div>
                            <div className="stat-item">                <span className="stat-label">⚡ Tiempo de Carga:</span>                <span className="stat-value">&lt; 500ms</span>              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <h2>ℹ️ Información de la Aplicación</h2>
              <p className="section-description">
                Detalles sobre la versión y características de tu centro de productividad.
              </p>
            </div>
            <div className="about-content">
              <div className="app-info">
                <div className="app-logo">🏢</div>
                <div className="app-details">
                  <h3>Personal Efficiency Center</h3>
                  <p className="version">Versión 1.0.0</p>
                  <p className="about-description">
                    Una aplicación moderna de productividad construida con React y ReactQuill, 
                    diseñada para la toma eficiente de notas, gestión de tareas, y organización personal.
                  </p>
                </div>
              </div>
              
              <div className="tech-stack">
                <h4>🛠️ Tecnologías Utilizadas:</h4>
                <div className="tech-grid">
                  <span className="tech-tag">React 18</span>
                  <span className="tech-tag">ReactQuill</span>
                  <span className="tech-tag">CSS3</span>
                  <span className="tech-tag">LocalStorage</span>
                  <span className="tech-tag">PWA Ready</span>
                  <span className="tech-tag">Responsive Design</span>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <div className="section-header">
              <h2>🔧 Acciones del Sistema</h2>
              <p className="section-description">
                Herramientas de mantenimiento y gestión de datos.
              </p>
            </div>
            <div className="system-actions">
              <button className="action-button primary">
                💾 Exportar Todas las Notas
              </button>
              <button className="action-button secondary">
                📥 Importar Configuración
              </button>
              <button className="action-button secondary">
                🧹 Limpiar Caché
              </button>
              <button className="action-button danger">
                🗑️ Resetear Aplicación
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
